/**
 * Accesso all'app di lei (ruolo `individuo`, fisso). Compare solo quando il server risponde 401:
 * il cookie di sessione dura a lungo (SESSION_TTL_HOURS), quindi in pratica lo vede una volta.
 * Stesso impianto del wizard (WizardFrame, direzione 03 Aria), nessun testo tecnico in faccia a lei.
 */
import { useId, useState, type FormEvent } from 'react'

import type { ApiError } from '@mamma/contracts'

import { api, ApiFailure } from '../api/client'
import { ErrorMessage, PrimaryButton, ProgressWords, QuestionTitle, WhyText, WizardFrame } from '../components'

const CAMPO = 'motion-colors block w-full rounded-field border-2 border-line bg-white px-4 py-3.5 text-body text-ink'

const RIMEDIO = 'Controlli nome e password e riprovi. Se non ricorda la password, chieda a chi la aiuta.'

const ERRORE_GENERICO: ApiError = {
  code: 'accesso_non_riuscito',
  message_it: 'Non sono riuscita a farla entrare.',
  remedy_it: RIMEDIO,
}

export function LoginScreen({ onLoggedIn }: { onLoggedIn: () => void }) {
  const userId = useId()
  const passId = useId()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<ApiError | null>(null)

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy || !username.trim() || !password) return
    setBusy(true)
    setError(null)
    try {
      await api.post('/auth/login', { username: username.trim(), password, role: 'individuo' })
      onLoggedIn()
    } catch (failure) {
      if (failure instanceof ApiFailure && failure.status === 0) setError(failure.error)
      else if (failure instanceof ApiFailure) setError({ ...failure.error, remedy_it: RIMEDIO })
      else setError(ERRORE_GENERICO)
    } finally {
      setBusy(false)
    }
  }

  return (
    <WizardFrame band={<ProgressWords block="Benvenuta" progress="Entri con nome e password" />}>
      <QuestionTitle>Entri nella sua app</QuestionTitle>
      <div className="mb-auto">
        <WhyText>Il nome e la password glieli ha dati chi la aiuta. Dopo la prima volta non li richiedo.</WhyText>
      </div>
      <form onSubmit={submit} noValidate data-testid="login">
        <label htmlFor={userId} className="text-soft mb-2 mt-[22px] block text-meta text-ink">
          Il suo nome utente
        </label>
        <input
          id={userId}
          className={CAMPO}
          autoComplete="username"
          autoCapitalize="none"
          autoCorrect="off"
          value={username}
          disabled={busy}
          onChange={(event) => setUsername(event.target.value)}
        />
        <label htmlFor={passId} className="text-soft mb-2 mt-[18px] block text-meta text-ink">
          La sua password
        </label>
        <input
          id={passId}
          type="password"
          className={CAMPO}
          autoComplete="current-password"
          value={password}
          disabled={busy}
          onChange={(event) => setPassword(event.target.value)}
        />
        <div className="mt-[22px]">
          <PrimaryButton type="submit" disabled={busy}>
            {busy ? 'Un momento…' : 'Entra'}
          </PrimaryButton>
        </div>
      </form>
      {error && (
        <div className="mt-4">
          <ErrorMessage error={error} retryLabel="Riprova" onRetry={() => setError(null)} />
        </div>
      )}
    </WizardFrame>
  )
}
